// src/pages/admin/Earnings.js
// src/pages/admin/Earnings.js
import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
} from "@mui/icons-material";

// Mock data for earnings
const earningsData = {
  monthly: [
    { month: 'Jan', revenue: 12500, expenses: 8500, profit: 4000 },
    { month: 'Feb', revenue: 13800, expenses: 9200, profit: 4600 },
    { month: 'Mar', revenue: 15200, expenses: 9800, profit: 5400 },
    { month: 'Apr', revenue: 14500, expenses: 8900, profit: 5600 },
    { month: 'May', revenue: 16800, expenses: 10200, profit: 6600 },
    { month: 'Jun', revenue: 18200, expenses: 11500, profit: 6700 },
  ],
  serviceBreakdown: [
    { name: 'Standard Cleaning', value: 45, revenue: 41040, color: '#0088FE' },
    { name: 'Deep Cleaning', value: 25, revenue: 22800, color: '#00C49F' },
    { name: 'Office Cleaning', value: 15, revenue: 13680, color: '#FFBB28' },
    { name: 'Move-In/Out', value: 10, revenue: 9120, color: '#FF8042' },
    { name: 'Specialty', value: 5, revenue: 4560, color: '#8884D8' },
  ],
  metrics: {
    totalRevenue: 91200,
    totalExpenses: 58100,
    netProfit: 33100,
    growthRate: 15.8,
    activeCustomers: 245,
    avgTransaction: 125,
    profitMargin: 36.3,
    expenseRatio: 63.7
  }
};

const ProgressBarWithLabel = ({ value, color, label, amount }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" fontWeight="600">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="600">
          ${amount?.toLocaleString()} ({value}%)
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={value} 
        sx={{ 
          height: 8, 
          borderRadius: 4,
          backgroundColor: alpha(color, 0.2),
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 4,
          }
        }}
      />
    </Box>
  );
};

const RevenueCard = ({ month, revenue, expenses, profit, theme }) => {
  const profitMargin = ((profit / revenue) * 100).toFixed(1);
  
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          {month}
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">Revenue</Typography>
            <Typography variant="body1" fontWeight="600" color="success.main">
              ${revenue.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">Expenses</Typography>
            <Typography variant="body1" fontWeight="600" color="error.main">
              ${expenses.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">Profit</Typography>
            <Typography variant="body1" fontWeight="600" color="primary.main">
              ${profit.toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
        <Box sx={{ mt: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={profitMargin} 
            color="primary"
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            Profit Margin: {profitMargin}%
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function Earnings() {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('monthly');

  const data = earningsData[timeRange];
  const { metrics } = earningsData;

  const stats = [
    {
      label: 'Total Revenue',
      value: `$${metrics.totalRevenue.toLocaleString()}`,
      change: `+${metrics.growthRate}%`,
      color: 'success',
      icon: <TrendingUpIcon />
    },
    {
      label: 'Total Expenses',
      value: `$${metrics.totalExpenses.toLocaleString()}`,
      change: '+12.3%',
      color: 'error',
      icon: <TrendingDownIcon />
    },
    {
      label: 'Net Profit',
      value: `$${metrics.netProfit.toLocaleString()}`,
      change: `+${metrics.growthRate}%`,
      color: 'primary',
      icon: <TrendingUpIcon />
    },
    {
      label: 'Avg. Transaction',
      value: `$${metrics.avgTransaction}`,
      change: '+5.2%',
      color: 'info',
      icon: <TrendingUpIcon />
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Earnings Report
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Export Report
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Comprehensive overview of revenue, expenses, and profit metrics
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color={`${stat.color}.main`} gutterBottom>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {stat.label}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    color: `${stat.color}.main`,
                    backgroundColor: alpha(theme.palette[stat.color].main, 0.1),
                    borderRadius: '50%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {stat.icon}
                  </Box>
                </Box>
                <Chip 
                  label={stat.change} 
                  size="small" 
                  color={stat.color}
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Chart Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value)}
                  startAdornment={<CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="outlined">This Month</Button>
                <Button variant="outlined">Last Month</Button>
                <Button variant="outlined">YTD</Button>
                <Button variant="outlined">Custom Range</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Revenue History */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Revenue History - {timeRange === 'monthly' ? 'Last 6 Months' : 'Last 4 Weeks'}
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {data.map((item, index) => (
                  <RevenueCard
                    key={index}
                    month={item.month}
                    revenue={item.revenue}
                    expenses={item.expenses}
                    profit={item.profit}
                    theme={theme}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Service Distribution */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Service Distribution
              </Typography>
              <Box sx={{ mb: 3 }}>
                {earningsData.serviceBreakdown.map((service, index) => (
                  <ProgressBarWithLabel
                    key={index}
                    value={service.value}
                    color={service.color}
                    label={service.name}
                    amount={service.revenue}
                  />
                ))}
              </Box>
              
              <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
                Service Performance
              </Typography>
              <List dense>
                {earningsData.serviceBreakdown.map((service, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: service.color
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={service.name}
                      secondary={`${service.value}% of revenue`}
                    />
                    <Typography variant="body2" fontWeight="600">
                      ${service.revenue.toLocaleString()}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Metrics */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Customer Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Active Customers</Typography>
                  <Typography variant="body2" fontWeight="600">{metrics.activeCustomers}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">New This Month</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">+24</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Retention Rate</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">78%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Avg. Jobs per Customer</Typography>
                  <Typography variant="body2" fontWeight="600">2.4</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Financial Health
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Profit Margin</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">{metrics.profitMargin}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Expense Ratio</Typography>
                  <Typography variant="body2" fontWeight="600" color="error.main">{metrics.expenseRatio}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Growth Rate</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">+{metrics.growthRate}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Revenue per Cleaner</Typography>
                  <Typography variant="body2" fontWeight="600">$3,800</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                <StarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Performance Insights
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Avg. Job Value</Typography>
                  <Typography variant="body2" fontWeight="600">${metrics.avgTransaction}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Completed Jobs</Typography>
                  <Typography variant="body2" fontWeight="600">728</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Customer Satisfaction</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">4.8/5</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Repeat Business Rate</Typography>
                  <Typography variant="body2" fontWeight="600" color="success.main">65%</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<DownloadIcon />}>
                Download Report
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<MoneyIcon />}>
                Tax Summary
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<TrendingUpIcon />}>
                Growth Analysis
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" fullWidth startIcon={<CalendarIcon />}>
                Schedule Report
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}